import { debounce, isEmpty } from 'lodash'
import { logger } from 'v2/utils/logger'
// @ts-expect-error - file exists
import Chord from '@shared/assets/sounds/Chord.mp3'
// @ts-expect-error - file exists
import Pulse from '@shared/assets/sounds/Pulse.mp3'
import { getConversationURL } from 'v2/pages/conversations/utils'
import type { Chunk } from 'v2/types/chunk.internal'
import type { RActiveConversation } from 'v2/types/api/conversations'
import { useAppStore } from 'v2/state/app'

interface NotificationOptions {
  title: string
  description: string
  onClick?: () => void
  sound?: string
  tag?: string
}

class BrowserNotificationManager {
  private static instance: BrowserNotificationManager

  private isPermissionGranted = false

  private isInitialDelayOver = false

  public init() {
    this.checkPermission()
    this.setInitialDelay()
  }

  public static getInstance(): BrowserNotificationManager {
    if (!BrowserNotificationManager.instance) {
      BrowserNotificationManager.instance = new BrowserNotificationManager()
    }
    return BrowserNotificationManager.instance
  }

  private checkPermission(): void {
    if (!('Notification' in window)) {
      logger.warn('This browser does not support desktop notification')
      return
    }

    if (Notification.permission === 'granted') {
      this.isPermissionGranted = true
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        this.isPermissionGranted = permission === 'granted'
      })
    }
  }

  private setInitialDelay(): void {
    setTimeout(() => {
      this.isInitialDelayOver = true
      logger.log('Initial delay to start notification manager is complete')
    }, 5000) // 5 seconds delay
  }

  private playSound(sound: string): void {
    try {
      const audioElement = !isEmpty(sound) ? new Audio(sound) : null
      if (audioElement) {
        audioElement.volume = 0.5
        audioElement.play()
      }
    } catch (error) {
      logger.error('Error playing notification sound:', error)
    }
  }

  public async sendNotification({
    title,
    description,
    onClick,
    sound = '',
    tag,
  }: NotificationOptions): Promise<void> {
    if (!this.isPermissionGranted || !this.isInitialDelayOver) {
      logger.warn('Notification not sent: permission not granted or initial delay not over')
      if (!this.isPermissionGranted) this.checkPermission()
      return
    }

    if (useAppStore.getState().appFocused) {
      logger.debug('Notification skipped: document is visible and focused')
      return
    }

    if (!window.cx.tabManager?.isLastUsedTab()) {
      logger.debug('Notification skipped: not the last used tab')
      return
    }

    try {
      const notification = new Notification(title, {
        body: description,
        icon: '/favicon.png',
        silent: true,
        tag: tag || undefined,
      })

      if (onClick) {
        notification.onclick = () => {
          window.focus()
          onClick()
          notification.close()
        }
      }

      // Attempt to play the sound
      this.playSound(sound)
    } catch (error) {
      logger.error('Error sending notification:', error)
    }
  }
}

export const browserNotificationManager = BrowserNotificationManager.getInstance()

export function sendNotification(options: NotificationOptions): void {
  // Debounce to prevent multiple notifications from being sent in quick succession
  // Also this allows the app to set states like 'isFocused' before the notification is sent
  debounce(() => browserNotificationManager.sendNotification(options), 500)()
}

const sounds = {
  messageRequest: Chord,
  message: Pulse,
}

// Add more notification types as needed
export const notify = {
  messageRequest: (contactName: string, conversationNumber: string) =>
    sendNotification({
      title: 'Incoming chat request',
      description: `From ${contactName}`,
      sound: sounds.messageRequest,
      tag: contactName,
      onClick: () => {
        if (window.cx.navigate) {
          window.cx.navigate(getConversationURL(conversationNumber, 'unassigned'), {
            replace: true,
          })
        }
      },
    }),
  chatMessage: (chunk: Chunk, conversation: RActiveConversation) =>
    sendNotification({
      description: chunk.label as string,
      sound: sounds.message,
      tag: chunk.id,
      title: `${conversation.contact_name}`,
      onClick: () => {
        if (window.cx.navigate) {
          window.cx.navigate(getConversationURL(conversation.conversation_number, 'active'), {
            replace: true,
          })
        }
      },
    }),
  incomingCall: (identifier: string) =>
    sendNotification({
      title: 'Incoming call',
      description: identifier,
      sound: sounds.messageRequest,
      onClick: () => {
        if (window.cx.navigate) {
          window.cx.navigate(getConversationURL('', 'active'), {
            replace: true,
          })
        }
      },
    }),
}
